import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wifi, KeyRound, Waves, Flame, Sparkles, LogOut, Search, ChevronDown,
  MapPin, Phone, MessageSquare, MessageCircle, ExternalLink, Copy, CheckCircle2, Instagram, Facebook,
  AlertTriangle, Menu, X,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Concierge from "./Concierge";

const GROUP_ORDER = ["Start here", "Amenities", "House rules", "Safety", "Checkout", "Local guide", "Help"];

export default function Guide() {
  const [data, setData] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [q, setQ] = useState("");
  const [conciergeOpen, setConciergeOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    http.get("/api/guide/property")
      .then((res) => setData(res.data))
      .catch(() => { toast.error("Please log in again"); nav("/login"); });
  }, []);

  const grouped = useMemo(() => {
    if (!data) return {};
    const g = {};
    for (const s of data.sections) { (g[s.group] ||= []).push(s); }
    return g;
  }, [data]);

  const filtered = useMemo(() => {
    if (!data || !q.trim()) return null;
    const needle = q.toLowerCase();
    // Synonym expansion
    const syns = { wifi: "wifi internet password network", spa: "spa hot tub jacuzzi", "hot tub": "hot tub spa jacuzzi",
      trash: "trash garbage bins", tv: "tv remote television", remote: "remote tv patio", dinner: "dinner food restaurant eat",
      food: "food dinner restaurant delivery grocery", checkout: "checkout leave depart" };
    const expanded = (syns[needle] || needle).split(" ");
    return data.sections.filter((s) => {
      const hay = (s.title + " " + s.body + " " + s.group).toLowerCase();
      return expanded.some((w) => hay.includes(w));
    });
  }, [q, data]);

  function openSection(id) {
    const willOpen = activeId !== id;
    setActiveId((cur) => (cur === id ? null : id));
    setNavOpen(false);
    if (!willOpen) return;
    // Wait for the accordion body to finish animating open, then position it:
    // - Panels that fit in the viewport: centered
    // - Tall panels that don't fit: anchor the top just below the sticky header
    setTimeout(() => {
      const el = document.getElementById(`sec-${id}`);
      if (!el) return;
      const headerH = document.querySelector("header")?.offsetHeight || 110;
      const visible = window.innerHeight - headerH;
      if (el.offsetHeight > visible) {
        const y = el.getBoundingClientRect().top + window.pageYOffset - headerH - 12;
        window.scrollTo({ top: y, behavior: "smooth" });
      } else {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 380);
  }

  async function copyWifi() {
    try {
      await navigator.clipboard.writeText(data.property.wifi_password);
      toast.success("Wi-Fi password copied");
    } catch { toast.error("Please copy manually: " + data.property.wifi_password); }
  }

  if (!data) {
    return <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center text-[#0E2A47] font-body">Loading your guide…</div>;
  }

  const P = data.property;
  const displayGroups = filtered ? { "Search results": filtered } : grouped;

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 bg-[#FAFAF8]/85 backdrop-blur-lg border-b border-[#0E2A47]/8">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center gap-4">
          <Link to="/" className="font-display font-extrabold text-[#0E2A47] text-lg tracking-tight">Grandview Estate</Link>
          <span className="text-xs text-[#0E2A47]/40 hidden sm:inline font-body font-semibold">Guest guide</span>
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="guide-concierge-open-header" onClick={() => setConciergeOpen(true)} className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#0E2A47] text-white text-sm px-4 py-2 font-display font-bold">
              <Sparkles size={14} strokeWidth={2.6} /> Ask the guide
            </button>
            <button data-testid="guide-logout" onClick={() => { logout(); nav("/"); }} className="inline-flex items-center gap-1.5 text-sm text-[#0E2A47]/60 hover:text-[#0E2A47] font-body font-semibold">
              <LogOut size={14} /> Sign out
            </button>
            <button className="lg:hidden p-2 -mr-2" onClick={() => setNavOpen((v) => !v)} data-testid="guide-nav-toggle">
              {navOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Nav strip — bold pill tabs */}
        <nav className={`${navOpen ? "block" : "hidden"} lg:block border-t border-[#0E2A47]/6 bg-[#FAFAF8]`}>
          <div className="max-w-6xl mx-auto px-5 py-3 flex flex-nowrap overflow-x-auto items-center gap-2 text-sm font-body scrollbar-thin">
            {GROUP_ORDER.filter((g) => grouped[g]).map((g) => (
              <a
                key={g}
                data-testid={`guide-nav-${g.replace(/\s+/g, "-").toLowerCase()}`}
                href={`#group-${g.replace(/\s+/g, "-")}`}
                onClick={(e) => { e.preventDefault(); const el = document.getElementById(`group-${g.replace(/\s+/g, "-")}`); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); setNavOpen(false); }}
                className="whitespace-nowrap rounded-full bg-white border-2 border-[#0E2A47]/10 px-5 py-2 font-display font-extrabold text-[#0E2A47] hover:border-[#2A7889] hover:text-[#2A7889] transition-colors"
              >
                {g}
              </a>
            ))}
          </div>
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-10 md:py-14">
        {/* Hero */}
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.22em] text-[#9F6B2E] font-body font-extrabold">Welcome{user?.email ? `, ${user.email}` : ""}</p>
          <h1 className="mt-4 font-display font-extrabold text-4xl md:text-6xl text-[#0E2A47] leading-tight">{P.hero_line_1}</h1>
          <p className="mt-4 text-[#0E2A47]/75 font-body font-medium max-w-2xl mx-auto text-lg">{P.hero_line_2}</p>
          <div className="mt-4 flex items-center justify-center gap-2 text-sm text-[#0E2A47]/70 font-body font-semibold">
            <MapPin size={14} className="text-[#2A7889]" strokeWidth={2.4} />
            <a href={P.map_url} target="_blank" rel="noreferrer" className="hover:text-[#2A7889]">{P.address}</a>
          </div>
        </div>

        {/* Search */}
        <div className="mt-10 max-w-2xl mx-auto relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#0E2A47]/40" />
          <input
            data-testid="guide-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search: wifi, remote, trash, hot tub, checkout…"
            className="w-full rounded-full border border-[#0E2A47]/12 bg-white pl-11 pr-11 py-3.5 text-[#0E2A47] font-body outline-none focus:border-[#1FB6C9] focus:ring-2 focus:ring-[#1FB6C9]/20 shadow-sm"
          />
          {q && (
            <button data-testid="guide-search-clear" onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[#0E2A47]/40 hover:text-[#0E2A47]"><X size={16} /></button>
          )}
        </div>

        {/* Quick actions — big, colored, bold */}
        {!filtered && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3">
          <QuickCard testid="qa-wifi" icon={Wifi} label="Wi-Fi" onClick={() => openSection("wifi")} bg="#0E2A47" />
          <QuickCard testid="qa-arrival" icon={KeyRound} label="Arrival & entry" onClick={() => openSection("arrival")} bg="#2A7889" />
          <QuickCard testid="qa-pool" icon={Waves} label="Pool & spa" onClick={() => openSection("pool-spa")} bg="#9F6B2E" />
          <QuickCard testid="qa-ask" icon={Sparkles} label="Ask the guide" onClick={() => setConciergeOpen(true)} bg="#1F4E5F" />
          <a data-testid="qa-rebook" href={P.booking_url} target="_blank" rel="noreferrer" className="group rounded-2xl p-6 flex flex-col items-start justify-between min-h-[140px] transition-transform duration-300 hover:-translate-y-0.5 shadow-lg col-span-2 md:col-span-2" style={{ backgroundColor: "#B57F35" }}>
            <Flame size={26} className="text-white" strokeWidth={2.4} />
            <div className="mt-4 font-display font-extrabold text-white text-xl leading-tight">Click to Rebook & Save 15%</div>
          </a>
        </div>)}

        {/* Wi-Fi copy card */}
        {!filtered && (
          <div className="mt-6 rounded-2xl bg-white border-2 border-[#0E2A47]/8 p-5 flex flex-wrap items-center gap-4">
            <Wifi size={22} className="text-[#2A7889]" strokeWidth={2.4} />
            <div className="text-base font-body text-[#0E2A47]">
              <span className="text-[#0E2A47]/60 font-semibold">Network</span> <b className="font-extrabold">{P.wifi_ssid}</b>
              <span className="mx-3 text-[#0E2A47]/25">·</span>
              <span className="text-[#0E2A47]/60 font-semibold">Password</span> <b className="font-extrabold">{P.wifi_password}</b>
            </div>
            <button data-testid="wifi-copy-btn" onClick={copyWifi} className="ml-auto inline-flex items-center gap-2 rounded-full bg-[#0E2A47] text-white text-sm px-5 py-2.5 font-display font-bold"><Copy size={14} /> Copy password</button>
          </div>
        )}

        {/* Groups & accordion sections */}
        <div className="mt-12 space-y-12">
          {Object.entries(displayGroups).map(([group, sections]) => (
            <section key={group} id={`group-${group.replace(/\s+/g, "-")}`} data-testid={`group-${group.replace(/\s+/g, "-").toLowerCase()}`}>
              <h2 className="font-display font-extrabold text-3xl md:text-4xl text-[#0E2A47] border-b-2 border-[#0E2A47]/10 pb-3 mb-4">{group}</h2>
              <div className="space-y-3">
                {sections.length === 0 && (
                  <div className="rounded-lg bg-white border border-[#0E2A47]/8 p-6 text-[#0E2A47]/70 font-body">
                    No matches. <button className="text-[#2A7889] font-bold hover:underline" onClick={() => setConciergeOpen(true)}>Ask the guide</button> or text (210) 274-3556.
                  </div>
                )}
                {sections.map((s) => (
                  <SectionPanel key={s.id} s={s} open={activeId === s.id} onToggle={() => openSection(s.id)} />
                ))}
              </div>
            </section>
          ))}
          {filtered && filtered.length === 0 && (
            <div className="rounded-lg bg-white border border-[#0E2A47]/8 p-8 text-center">
              <p className="font-display text-lg text-[#0E2A47]">Nothing matched "{q}"</p>
              <p className="text-sm text-[#0E2A47]/60 font-body mt-2">Try a different word, or ask the concierge below.</p>
              <button onClick={() => setConciergeOpen(true)} className="mt-4 rounded-full bg-[#1FB6C9] text-white px-6 py-2.5 font-display">Ask the guide</button>
            </div>
          )}
        </div>

        {/* Contact footer */}
        <footer className="mt-16 rounded-2xl bg-[#0E2A47] text-white p-8 md:p-10">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="font-display text-xl">Need something now?</div>
              <p className="mt-2 text-white/70 font-body text-sm">Message us through the booking platform. For urgent issues, call or text.</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <a data-testid="host-call-btn" href={`tel:${P.phone_e164}`} className="inline-flex items-center gap-2 rounded-full bg-[#1FB6C9] px-5 py-2.5 font-display text-sm"><Phone size={14} /> Call</a>
                <a data-testid="host-text-btn" href={`sms:${P.phone_e164}`} className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-5 py-2.5 font-display text-sm"><MessageSquare size={14} /> Text</a>
              </div>
            </div>
            <div>
              <div className="font-display text-xl">Rebook direct</div>
              <p className="mt-2 text-white/70 font-body text-sm">Save 15% versus other platforms.</p>
              <a data-testid="rebook-footer-btn" href={P.booking_url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#d9a441] text-[#0E2A47] px-5 py-2.5 font-display text-sm font-medium">
                Click to Rebook & Save 15% <ExternalLink size={14} />
              </a>
            </div>
            <div>
              <div className="font-display text-xl">Follow along</div>
              <div className="mt-4 flex gap-3">
                <a data-testid="follow-fb" href={P.facebook} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-5 py-2.5 font-display text-sm"><Facebook size={14} /> Follow us on Facebook</a>
                <a data-testid="follow-ig" href={P.instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-5 py-2.5 font-display text-sm"><Instagram size={14} /> Follow us on Instagram</a>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-white/10 text-xs text-white/50 font-body flex items-center justify-between flex-wrap gap-2">
            <span>Grandview Estate · Managed by Elite STR Hosting Co.</span>
            <span>911 first for emergencies. Host: {P.phone_display}</span>
          </div>
        </footer>
      </main>

      {/* Floating Ask button (mobile) */}
      <button
        data-testid="guide-concierge-floating"
        onClick={() => setConciergeOpen(true)}
        className="fixed bottom-6 right-6 z-30 h-14 w-14 rounded-full bg-[#1FB6C9] text-white shadow-2xl shadow-[#1FB6C9]/30 flex items-center justify-center hover:scale-105 transition-transform"
        aria-label="Open concierge"
      >
        <MessageCircle size={22} />
      </button>

      <Concierge open={conciergeOpen} onClose={() => setConciergeOpen(false)} />
    </div>
  );
}

function QuickCard({ icon: Icon, label, onClick, testid, bg = "#0E2A47" }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="group rounded-2xl p-6 flex flex-col items-start justify-between min-h-[140px] transition-transform duration-300 hover:-translate-y-0.5 shadow-lg"
      style={{ backgroundColor: bg }}
    >
      <Icon size={26} className="text-white" strokeWidth={2.4} />
      <div className="mt-4 font-display font-extrabold text-white text-xl leading-tight text-left">{label}</div>
    </button>
  );
}

function SectionPanel({ s, open, onToggle }) {
  return (
    <div id={`sec-${s.id}`} data-testid={`section-${s.id}`} className="rounded-xl bg-white border-2 border-[#0E2A47]/10 overflow-hidden">
      <button
        onClick={onToggle}
        data-testid={`section-toggle-${s.id}`}
        aria-expanded={open}
        className="w-full flex items-center justify-between text-left px-5 py-5 hover:bg-[#F1E8DA]/40 transition-colors"
      >
        <div className="flex items-center gap-3">
          {s.group === "Safety" && <AlertTriangle size={18} className="text-amber-600 shrink-0" strokeWidth={2.4} />}
          <span className="font-display font-extrabold text-[#0E2A47] text-lg">{s.title}</span>
        </div>
        <ChevronDown size={22} className={`text-[#2A7889] transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2.6} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-1 font-body text-[#0E2A47]/85 whitespace-pre-line leading-relaxed border-t-2 border-[#0E2A47]/6 text-[15px]">
              {s.body}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
